import React, { useMemo, useState } from 'react';
import { ProductInventoryPageQuery } from '../../../../../services';
import {
    Continued,
    CTableWrapper,
    DBlock,
    DBlockHeader,
    Discontinued,
    DTextField,
    EditIcon,
} from '../../../../dum';
import { formatDate, formatFloat, useString } from '../../../../../helpers';
import { SUpsertProductDialog } from '../../../../smart';
import { Box, IconButton } from '@mui/material';
import { ProductInventoryMovementsPanel } from './product-inventory-movements-panel';

interface IInventoryByProduct {
    productsInventory: ProductInventoryPageQuery['getProductsInventory'];
    isDetailed: boolean;
}

export function InventoryByProduct({
    productsInventory,
    isDetailed,
}: IInventoryByProduct) {
    const [open, setOpen] = useState(false);
    const [productId, setProductId] = useState<number | null>(null);

    const [filter, setFilter] = useString({
        KEY: InventoryByProduct.name + '_Filter',
    });

    const filteredProductsInventory = useMemo(() => {
        return productsInventory.filter((pi) => {
            return filter !== ''
                ? pi.product?.compound_description
                      .toLowerCase()
                      .includes(filter.toLowerCase())
                : true;
        });
    }, [productsInventory, filter]);

    return (
        <>
            <DBlock sx={{ flexGrow: 0, minHeight: 'auto' }}>
                <DBlockHeader subtitle={'Por producto'} />
                <Box sx={{ mb: 2 }}>
                    <DTextField
                        label={'Filtro'}
                        onChange={setFilter}
                        value={filter}
                    />
                </Box>
                <Box
                    sx={{
                        '& .MuiTableContainer-root': {
                            maxHeight: '56vh',
                            overflow: 'auto',
                        },
                    }}
                >
                <CTableWrapper
                    stickyHeader
                    items={[
                        {
                            title: 'Producto',
                            textAlign: 'left',
                            cellGrow: 10,
                            content: (item) => {
                                return isDetailed
                                    ? item.product?.compound_description
                                    : item.product?.external_description;
                            },
                        },
                        {
                            title: 'Kilos',
                            content: (item) => {
                                return formatFloat(item.kilos);
                            },
                            textAlign: 'right',
                        },
                        {
                            title: 'Grupos',
                            content: (item) => {
                                return formatFloat(item.groups);
                            },
                            textAlign: 'right',
                        },
                        {
                            title: 'Ultima actualización',
                            cellGrow: 5,
                            textAlign: 'center',
                            content: (item) => {
                                return item.last_update
                                    ? formatDate(item.last_update)
                                    : '-';
                            },
                        },
                        {
                            title: 'Estatus',
                            content: (item) => {
                                return item.product?.discontinued ? (
                                    <Discontinued color={'error'} />
                                ) : (
                                    <Continued color={'success'} />
                                );
                            },
                        },
                        {
                            title: '',
                            textAlign: 'center',
                            content: (item) => {
                                return (
                                    <IconButton
                                        size="small"
                                        sx={{ bgcolor: 'rgba(34,197,94,0.15)', boxShadow: 2, borderRadius: '50%' }}
                                        disabled={!!item.product_id}
                                        onClick={() => {
                                            setProductId(item?.product_id || 0);
                                            setOpen(true);
                                        }}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                );
                            },
                        },
                    ]}
                    data={filteredProductsInventory}
                    // Per-product ledger. The panel — and therefore its query —
                    // only mounts when a row is expanded, so opening one product
                    // never fetches movements for the rest of the list.
                    collapse={(item) =>
                        item.product_id ? (
                            <ProductInventoryMovementsPanel
                                productId={item.product_id}
                            />
                        ) : (
                            'Este producto no tiene id para consultar movimientos.'
                        )
                    }
                />
                </Box>
            </DBlock>
            <SUpsertProductDialog
                open={open}
                setOpen={setOpen}
                productId={productId}
            />
        </>
    );
}
